// src/components/Layout.js

import React from 'react';
import { Link } from 'gatsby';
import { useLocation } from '@reach/router';
import { useAudioPlayer, PlayerUI } from './AudioPlayer';
import { useLanguage } from './LanguageProvider';
import { useAuth } from './AuthProvider';
import { useUnseenFeedbackCount } from '../hooks/useUnseenFeedbackCount';
import { useNotifications } from '../hooks/useNotifications';
import { usePromotionAck } from '../hooks/usePromotionAck';
import { useMentorAssignedAck } from '../hooks/useMentorAssignedAck';
import { useFrutoGraduatesCount } from '../hooks/useFrutoGraduatesCount';
import { getNotificationTypeMeta } from '../utils/notificationTypeMeta';
import { SHOW_HOY } from '../config/cuenta';
import NotificationCapsule from './NotificationCapsule';
import FeedbackWidget from './FeedbackWidget';
import SubscribeCard from './subscribe/SubscribeCard';
import { captureSubscriptionFromUrl } from '../hooks/useSubscriptionState';
// S19: `Seo` ya NO se importa aquí. Vive solo dentro de `export function Head()` de cada
// página (S18). Renderizarlo en el cuerpo emitía `<html>`, `<title>` y `<meta>` dentro de un
// `<div>` del `<body>`: el parser del navegador descarta ese `<html>`, así que el primer hijo
// que React esperaba no existía y la hidratación fallaba (React #418 → #423). Ver
// project-log/sesiones/S19-html-vacio-hasmounted.md.
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';
import { functions, db } from '../services/firebase';
import { logoutUser } from '../services/auth-service';
import { getSiblingUrl, localizedUrl, buildPath, getBuildLanguage } from '../utils/siteUrls';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';

// Botón flotante de mensajes/reportes: oculto (2026-10-14), ver el render más abajo.
const SHOW_FEEDBACK_WIDGET = false;

// Icons
const BookOpenIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}> <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" /> </svg>);
const PlayCircleIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}> <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /> <path strokeLinecap="round" strokeLinejoin="round" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z" /> </svg>);
const UsersIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}> <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /> </svg>);
const DocumentTextIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}> <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /> </svg>);


const NewspaperIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" /></svg>);
const UserCircleIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" /></svg>);
const HomeIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>);
const ChevronDownIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>);
const GlobeIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18z" /><path strokeLinecap="round" strokeLinejoin="round" d="M3.6 9h16.8M3.6 15h16.8M12 3a15.3 15.3 0 010 18M12 3a15.3 15.3 0 000 18" /></svg>);
const MicrophoneIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" /></svg>);
const ShieldCheckIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" /></svg>);

// Íconos de marca del footer (fill, no stroke — son logos, no íconos de UI)
const YouTubeIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M23.5 6.19a3.02 3.02 0 00-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 00.5 6.19C0 8.08 0 12 0 12s0 3.92.5 5.81a3.02 3.02 0 002.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 002.12-2.14C24 15.92 24 12 24 12s0-3.92-.5-5.81zM9.55 15.57V8.43L15.82 12l-6.27 3.57z" /></svg>);
const WhatsAppIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.42-.07-.13-.27-.2-.57-.35zM12.05 21.7h-.01a9.6 9.6 0 01-4.9-1.34l-.35-.21-3.64.95.97-3.55-.23-.36a9.57 9.57 0 01-1.47-5.12c0-5.3 4.32-9.6 9.63-9.6a9.56 9.56 0 016.8 2.82 9.5 9.5 0 012.81 6.79c0 5.3-4.32 9.61-9.62 9.61zM20.5 3.49A11.9 11.9 0 0012.05 0C5.46 0 .1 5.35.1 11.92c0 2.1.55 4.15 1.6 5.96L0 24l6.28-1.64a11.97 11.97 0 005.76 1.46h.01c6.59 0 11.95-5.35 11.95-11.92a11.83 11.83 0 00-3.5-8.41z" /></svg>);
const EnvelopeIcon = (props) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" aria-hidden="true" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>);

const YOUTUBE_URL = "https://www.youtube.com/@vocesdesperanza025";
const WHATSAPP_URL = "https://wa.me/522223614495";
const CONTACT_EMAIL_ES = "voces.deesperanza025@gmail.com";
const CONTACT_EMAIL_EN = "voices.ofhope025@gmail.com";

// Rutas públicas donde puede aparecer la tarjeta flotante del devocional por correo
// (captacion-devocional-y-boletin.md, Fase 2). Lista cerrada a propósito: nunca en la
// plataforma (mi-cuenta, estudios, paneles), ni en /newsletter/* (ya es la página del
// formulario), ni en páginas legales o de campaña de donación.
const SUBSCRIBE_CARD_ROUTES = [
  /^\/$/,
  /^\/devocionales\/?$/,
  /^\/videos\/?$/,
  /^\/ministerio(\/.*)?$/,
  /^\/fruto\/?$/,
  /^\/equipo\/?$/,
  /^\/formacion\/?$/,
  /^\/tienda\/?$/,
];

export default function Layout({ children }) {
  const { track } = useAudioPlayer();
  const { t, language, isEnglish, changeLanguage } = useLanguage();
  const CONTACT_EMAIL = isEnglish ? CONTACT_EMAIL_EN : CONTACT_EMAIL_ES;
  const [langMenuOpen, setLangMenuOpen] = React.useState(false);
  const langMenuRef = React.useRef(null);

  // Toggle de idioma del navbar — mismo patrón que devocionales.js (dropdown de globo,
  // cambia i18next en el momento y persiste la preferencia vía Preferences).
  React.useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setLangMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [langMenuOpen]);
  const { user, profile } = useAuth();
  const isAdmin = profile?.role === "admin";
  const isVoiceMember = Boolean(profile?.voice?.languages?.length);
  const { count: unseenFeedbackCount } = useUnseenFeedbackCount(user?.uid);
  const { pending: pendingPromotion, dismiss: dismissPromotion } = usePromotionAck(user?.uid, profile?.role, profile?.roleAckedAs);
  const { pending: pendingMentorAssigned, dismiss: dismissMentorAssigned } = useMentorAssignedAck(user?.uid, profile?.mentorId, profile?.mentorAckedId);
  const pendingGraduatesCount = useFrutoGraduatesCount(isAdmin);
  // Fase 4 de notificaciones-sistema.md: inbox in-app del bus (Fase 2/3). Aparece junto
  // al aviso agregado de unseenFeedbackCount de abajo — puede haber solape momentáneo
  // para mentor_feedback (mismo evento, dos mecanismos) hasta que se decida retirar el
  // viejo; decisión explícita de Víctor de dejarlo así por ahora, no un descuido.
  const { notifications: busNotifications, markAsRead: markNotificationRead } = useNotifications(user?.uid);

  // Con "Hoy" archivado (SHOW_HOY = false) se apagan los avisos que llevaban a Hoy o al
  // panel de mentor: comentario del mentor, ascenso, mentor asignado, FRUTO y lecciones.
  const HOY_ONLY_TYPES = ['lesson_completed', 'program_milestone', 'welcome'];
  const notificationSignals = [
    SHOW_HOY && unseenFeedbackCount > 0 && {
      id: 'feedback',
      chip: 'sage',
      chipLabel: t('notifications.chipFeedback'),
      label: t('studies.unseenFeedback', { count: unseenFeedbackCount }),
      href: '/mi-cuenta/',
    },
    ...busNotifications.filter((n) => SHOW_HOY || !HOY_ONLY_TYPES.includes(n.type)).map((n) => {
      const meta = getNotificationTypeMeta(n.type, t);
      // S14b: 'lesson_completed' es accionable — no se descarta con un clic normal, solo
      // se marca leída cuando el mentor completa /mentor/reconocer/[id]/. El resto de los
      // tipos conserva el dismiss inmediato de siempre.
      const isSticky = n.type === 'lesson_completed';
      return {
        id: `bus-${n.id}`,
        chip: meta.chip,
        chipLabel: meta.chipLabel,
        label: n.payload?.title || '',
        href: meta.href,
        ...(isSticky ? {} : { onDismiss: () => markNotificationRead(n.id) }),
      };
    }),
    SHOW_HOY && pendingPromotion && {
      id: 'promotion',
      chip: 'gold',
      chipLabel: t('notifications.chipPromotion'),
      label: t('discipulo.promotedBannerTitle'),
      href: '/mi-cuenta/',
      onDismiss: dismissPromotion,
    },
    SHOW_HOY && pendingMentorAssigned && {
      id: 'mentor',
      chip: 'forest',
      chipLabel: t('notifications.chipMentor'),
      label: t('discipulo.mentorAssignedBannerTitle'),
      href: '/mi-cuenta/',
      onDismiss: dismissMentorAssigned,
    },
    SHOW_HOY && isAdmin && pendingGraduatesCount > 0 && {
      id: 'graduates',
      chip: 'gold',
      chipLabel: t('notifications.chipAdmin'),
      label: t('notifications.graduatesPending', { count: pendingGraduatesCount }),
      href: '/admin/',
    },
  ].filter(Boolean);

  const location = useLocation();
  const isHome = location.pathname === '/';
  // Páginas que abren con una franja verde a todo lo ancho (Videos, Ministerio, Noticias): sin
  // el margen superior de las páginas normales, que dejaba una línea clara entre la barra y
  // la franja (feedback Víctor 2026-09-30). Rutas en ES y EN.
  const startsWithBand = /^\/(videos|ministerio|ministry|ministerio\/historias|ministry\/stories)\/?$/.test(location.pathname);
  const isDevotionalPage = location.pathname.startsWith('/devocionales');
  const showSubscribeCard = SUBSCRIBE_CARD_ROUTES.some((re) => re.test(location.pathname));

  // Quien llega desde un link de un correo nuestro ya está inscrito: la invitación se
  // encoge en vez de volver a pedirle el correo (useSubscriptionState).
  React.useEffect(() => {
    captureSubscriptionFromUrl(location.search);
  }, [location.search]);
  const [isPWA, setIsPWA] = React.useState(false);
  const [isNativeApp, setIsNativeApp] = React.useState(false);
  const [isIOS, setIsIOS] = React.useState(false);
  const [navHidden, setNavHidden] = React.useState(false);
  // Idioma destino esperando confirmación (solo con sesión, en web): cruzar de dominio cierra
  // la sesión, así que primero se avisa. null = sin aviso abierto.
  const [pendingLangSwitch, setPendingLangSwitch] = React.useState(null);
  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false);
  const accountMenuRef = React.useRef(null);
  const [mobileAccountMenuOpen, setMobileAccountMenuOpen] = React.useState(false);
  const mobileAccountMenuRef = React.useRef(null);
  const [mobileLangMenuOpen, setMobileLangMenuOpen] = React.useState(false);
  const mobileLangMenuRef = React.useRef(null);

  // Aviso de cambio de idioma — Escape equivale a "Quedarme aquí".
  React.useEffect(() => {
    if (!pendingLangSwitch) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setPendingLangSwitch(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [pendingLangSwitch]);

  // Desplegable "Mi cuenta" (solo desktop, con sesión) — agrupa Ajustes/Admin/Cerrar sesión en
  // un solo control en vez de 3 piezas sueltas. project-log/initiatives/barra-cuenta-descubrible.md
  React.useEffect(() => {
    if (!accountMenuOpen) return;
    const handleClickOutside = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setAccountMenuOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [accountMenuOpen]);

  // Mismo patrón, versión móvil del menú "Mi cuenta" — ref propio para no chocar con el
  // click-outside del dropdown de escritorio (son dos elementos DOM distintos).
  React.useEffect(() => {
    if (!mobileAccountMenuOpen) return;
    const handleClickOutside = (e) => {
      if (mobileAccountMenuRef.current && !mobileAccountMenuRef.current.contains(e.target)) {
        setMobileAccountMenuOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMobileAccountMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileAccountMenuOpen]);

  // Cambiador de idioma de la barra móvil — mismo componente conceptual que el del navbar de
  // escritorio, pero con su propio ref/estado: ambos existen en el DOM a la vez (uno se oculta
  // por CSS según el breakpoint), así que no pueden compartir la misma ref de click-outside.
  React.useEffect(() => {
    if (!mobileLangMenuOpen) return;
    const handleClickOutside = (e) => {
      if (mobileLangMenuRef.current && !mobileLangMenuRef.current.contains(e.target)) {
        setMobileLangMenuOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMobileLangMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileLangMenuOpen]);

  // === 2. AÑADE ESTE useEffect PARA MANEJAR LA STATUS BAR ===
  React.useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const setStatusBarStyle = (isDark) => {
        // En iOS/Capacitor: Style.Light = texto NEGRO, Style.Dark = texto BLANCO (al revés de lo esperado)
        if (isDark) {
          StatusBar.setStyle({ style: Style.Dark }).catch(() => { }); // Texto blanco sobre fondo oscuro
          StatusBar.setBackgroundColor({ color: '#0F2318' }).catch(() => { }); // Verde oscuro (brand-dark)
        } else {
          StatusBar.setStyle({ style: Style.Light }).catch(() => { }); // Texto negro sobre fondo claro
          StatusBar.setBackgroundColor({ color: '#FFF8E7' }).catch(() => { }); // Parchment — igual que el fondo del app en modo claro
        }
      };

      // Establece el estilo inicial basado en la clase 'dark' en <html>
      const isInitiallyDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
      setStatusBarStyle(isInitiallyDark);

      // Observa cambios en la clase 'dark' del <html> para actualizar estilo en tiempo real
      if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'class') {
              const isNowDark = mutation.target.classList.contains('dark');
              setStatusBarStyle(isNowDark);
            }
          });
        });

        observer.observe(document.documentElement, { attributes: true });

        return () => observer.disconnect();
      }
    }
  }, []);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      setIsPWA(!!standalone);
      setIsNativeApp(Capacitor.isNativePlatform());
      setIsIOS(Capacitor.getPlatform() === 'ios');
    }
    // Ocultar splash screen si el plugin está disponible (evita errores en web)
    if (Capacitor.isPluginAvailable && Capacitor.isPluginAvailable('SplashScreen')) {
      SplashScreen.hide();
    }
  }, []);

  // Navbar auto-hide en desktop — solo en /devocionales/: se oculta al bajar, reaparece al subir.
  React.useEffect(() => {
    if (!isDevotionalPage || typeof window === 'undefined') {
      setNavHidden(false);
      return;
    }
    let lastScrollY = window.scrollY;
    const SCROLL_THRESHOLD = 10;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY;
      if (Math.abs(delta) > SCROLL_THRESHOLD) {
        setNavHidden(delta > 0 && currentScrollY > 80);
        lastScrollY = currentScrollY;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDevotionalPage]);

  // Push Notifications Registration — el permiso se pide recién tras interactuar
  // con el devocional (nota o favorito, ver PersonalNote.js), no en frío al abrir la app.
  React.useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      let requested = false;

      const setupPushNotifications = async () => {
        if (requested) return;
        requested = true;
        try {
          const permResult = await PushNotifications.checkPermissions();
          if (permResult.receive === 'prompt') {
            const requestResult = await PushNotifications.requestPermissions();
            if (requestResult.receive !== 'granted') return;
          } else if (permResult.receive === 'denied') {
            return;
          }
          await PushNotifications.register();
        } catch (error) {
          console.error('Error setting up push notifications:', error);
        }
      };

      Preferences.get({ key: 'hasEngagedWithDevotional' }).then(({ value }) => {
        if (value === 'true') setupPushNotifications();
      });
      window.addEventListener('vde:devotional-engaged', setupPushNotifications);

      const registrationListener = PushNotifications.addListener(
        'registration',
        async (token) => {
          let tokenForFirebase = token.value;
          if (Capacitor.getPlatform() === 'ios') {
            try {
              const fcm = await FCM.getToken();
              tokenForFirebase = fcm.token;
            } catch (err) {
              console.error('Error getting FCM token on iOS:', err);
            }
          }
          await Preferences.set({ key: 'fcm_token', value: tokenForFirebase });
          const { value } = await Preferences.get({ key: 'language' });
          subscribeToTopics(value || 'es', tokenForFirebase);
        }
      );

      const errorListener = PushNotifications.addListener(
        'registrationError',
        (error) => { console.error('Error on registration:', JSON.stringify(error)); }
      );

      const receivedListener = PushNotifications.addListener(
        'pushNotificationReceived',
        (notification) => { console.log('Push notification received:', notification); }
      );

      const actionListener = PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification) => { handleNotificationTap(notification); }
      );

      return () => {
        window.removeEventListener('vde:devotional-engaged', setupPushNotifications);
        registrationListener.remove();
        errorListener.remove();
        receivedListener.remove();
        actionListener.remove();
      };
    }
  }, []);

  // Sincroniza el token FCM guardado localmente con el perfil de Firestore
  // del usuario autenticado, para poder enviarle notificaciones dirigidas.
  React.useEffect(() => {
    if (!user || !Capacitor.isNativePlatform()) return;
    const syncToken = async () => {
      try {
        const { value: token } = await Preferences.get({ key: 'fcm_token' });
        if (!token) return;
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, { fcmToken: token }, { merge: true });

        // Fase 1 de notificaciones-sistema.md: fcmTokens[] en paralelo al campo singular
        // fcmToken de arriba, sin reemplazarlo — sendTargetedNotification.js sigue leyendo
        // el singular por ahora. Soluciona que un segundo dispositivo pise el token del
        // primero (.claude/skills/paneles-ia.md:220). Dedupe manual por token (no arrayUnion):
        // arrayUnion compara el objeto completo, y updatedAt cambia en cada login, así que
        // arrayUnion agregaría una entrada nueva cada vez en lugar de refrescar la existente.
        const snap = await getDoc(userRef);
        const existing = Array.isArray(snap.data()?.fcmTokens) ? snap.data().fcmTokens : [];
        const refreshed = existing.filter((entry) => entry.token !== token);
        refreshed.push({ token, platform: Capacitor.getPlatform(), updatedAt: Timestamp.now() });
        await setDoc(userRef, { fcmTokens: refreshed }, { merge: true });
      } catch (error) {
        console.error('Error syncing FCM token to Firestore:', error);
      }
    };
    syncToken();
  }, [user]);

  // Fase 1 de notificaciones-sistema.md: espeja las preferencias locales de tópicos push
  // (Capacitor Preferences, por dispositivo) a users/{uid}.notificationPrefs (por persona),
  // sin tocar el modelo local — cubre el caso real de "se pidió permiso de push antes de
  // iniciar sesión" (el permiso se pide tras interactuar con un devocional, no al abrir la
  // app) además de iniciar sesión con prefs locales ya guardadas. ajustes.js escribe el mismo
  // campo en caliente cuando cambia un toggle; este efecto solo cubre el caso "ya había algo
  // guardado localmente y recién ahora hay sesión para atribuírselo".
  React.useEffect(() => {
    if (!user || !Capacitor.isNativePlatform()) return;
    const syncPrefs = async () => {
      try {
        const { value } = await Preferences.get({ key: 'notification_topics' });
        if (!value) return;
        const localPrefs = JSON.parse(value);
        await setDoc(
          doc(db, 'users', user.uid),
          {
            notificationPrefs: {
              daily_devotional: localPrefs.daily_devotional ?? true,
              new_videos: localPrefs.new_videos ?? true,
              special_resources: localPrefs.special_resources ?? true,
              language: localPrefs.language || language,
              updatedAt: Timestamp.now(),
            },
          },
          { merge: true }
        );
      } catch (error) {
        console.error('Error syncing notification prefs to Firestore:', error);
      }
    };
    syncPrefs();
  }, [user]);

  const subscribeToTopics = async (language, token) => {
    if (!token) return;
    try {
      await Preferences.set({
        key: 'notification_topics',
        value: JSON.stringify({
          daily_devotional: true,
          new_videos: true,
          special_resources: true,
          language,
        })
      });
      const subscribeToTopic = httpsCallable(functions, 'subscribeToTopic');
      const lang = language === 'es' ? 'es' : 'en';
      const topics = [`daily_devotional_${lang}`, `new_videos_${lang}`, `special_resources_${lang}`];
      for (const topic of topics) {
        try {
          await subscribeToTopic({ token, topic });
        } catch (e) {
          console.error(`Failed to subscribe to ${topic}`, e);
        }
      }
    } catch (error) {
      console.error('Error subscribing to topics:', error);
    }
  };

  // Toggle de idioma: en páginas públicas y solo en web (nunca en la app nativa, que no
  // tiene concepto de dominio), cambiar de idioma navega de una vez al dominio hermano en
  // el slug correspondiente — así la URL nunca queda desincronizada del idioma mostrado (el
  // bug que reportó el equipo: compartir una historia en inglés seguía dando la URL de
  // voces-de-esperanza.com). En rutas protegidas (mi-cuenta, estudios, mentor, admin...) NO
  // se navega — Firebase Auth guarda la sesión por origin, y un redirect ahí desloguearía al
  // usuario; el idioma solo cambia en memoria, igual que antes.
  //
  // S20 — el comparador es el idioma del BUILD, no `language`. Antes se comparaba contra el
  // toggle en memoria y se calculaba el destino invirtiéndolo, así que estando en el dominio
  // ES con la interfaz ya en inglés (lo único que se puede hacer dentro de una ruta
  // protegida) el menú quedaba trabado: "English" no hacía nada (targetLang === language) y
  // "Spanish" recargaba el MISMO dominio. Ahora el destino es explícito y el toggle siempre
  // tiene salida.
  //
  // 2026-09-23 (pedido de Víctor): con sesión abierta, cruzar al otro dominio ya no es
  // silencioso ni se evita — se avisa primero que la sesión no viaja (Firebase Auth la guarda
  // por origin) y, si acepta, se navega. Esto reemplaza el cambio "solo en memoria" que había
  // en rutas protegidas: se perdía al recargar y dejaba la interfaz en un idioma distinto al
  // del dominio. Sin sesión no hay nada que perder, así que se navega directo.
  const handleLanguageSelect = (targetLang) => {
    const buildLang = getBuildLanguage();

    // App nativa: no hay dominios que cruzar, el toggle es el cambio real.
    if (Capacitor.isNativePlatform()) {
      if (targetLang !== language) changeLanguage(targetLang);
      return;
    }

    // Mismo idioma del build: basta con re-sincronizar la interfaz.
    if (targetLang === buildLang) {
      if (targetLang !== language) changeLanguage(targetLang);
      return;
    }

    if (user) {
      setPendingLangSwitch(targetLang);
      return;
    }
    window.location.href = localizedUrl(location.pathname, targetLang);
  };

  const confirmLangSwitch = () => {
    const targetLang = pendingLangSwitch;
    setPendingLangSwitch(null);
    if (targetLang) window.location.href = localizedUrl(location.pathname, targetLang);
  };

  const handleNotificationTap = (notificationAction) => {
    const data = notificationAction.notification.data;
    if (typeof window === 'undefined') return;
    if (data.type === 'daily_devotional' && data.date) {
      window.location.href = `/devocionales/?date=${data.date}`;
    } else if (data.type === 'new_video' && data.videoId) {
      window.location.href = `/videos/?highlight=${data.videoId}`;
    } else if (data.type === 'special_resource' && data.url) {
      window.location.href = data.url;
    }
  };

  // S19: aquí vivía `if (!hasMounted) return <div .../>`, que vaciaba TODO el sitio en el
  // HTML generado (1 carácter de texto visible). Tapaba el fallo de hidratación que causaba
  // el `<Seo />` del cuerpo, ya retirado arriba. Sin ese `<Seo />` no queda ninguna diferencia
  // servidor/cliente en el primer render: `user` es null en los dos, y `language` sale del
  // flag del build en Node y del hostname en el navegador — un dominio sirve un solo build,
  // así que coinciden. Por eso el gate no sobrevive ni siquiera acotado a un fragmento.

  // Ítem 1 auth-aware: sin sesión es "Inicio" (/), con sesión es "Hoy" (/mi-cuenta/) — mismo
  // array, misma cantidad de ítems (5). No reabre la decisión de los 5 navItems.
  // 2026-09-23 (pedido de Víctor): Formación se oculta del nav (la página sigue viva y
  // enlazada desde el contenido) y su lugar lo toma Noticias, que antes vivía en el
  // desplegable de Ministerio. La ruta de Noticias se queda en /ministerio/historias/ para no
  // romper links ya compartidos ni los del boletín.
  // 2026-10-26 (cuenta-simple-equipo.md): con SHOW_HOY apagado, el ítem 1 es "Inicio" también
  // con sesión. La cuenta se abre desde "Mi cuenta" en la cabecera.
  const showHoyNav = Boolean(user) && SHOW_HOY;
  const homePath = showHoyNav ? "/mi-cuenta/" : "/";
  const ministryPath = buildPath("/ministerio/");
  const newsPath = buildPath("/ministerio/historias/");
  const navItems = [
    { name: showHoyNav ? t('notifications.today') : t('navigation.home'), path: homePath, icon: HomeIcon },
    { name: t('navigation.devotionals'), path: buildPath("/devocionales/"), icon: BookOpenIcon },
    { name: t('navigation.videos'), path: "/videos/", icon: PlayCircleIcon },
    { name: t('navigation.ministry'), path: ministryPath, icon: UsersIcon },
    { name: t('navigation.ministryStories'), path: newsPath, icon: NewspaperIcon },
  ];

  // Noticias vive debajo de /ministerio/, así que partiallyActive marcaría los dos a la vez.
  // Ministerio queda activo en su subárbol salvo en Noticias.
  const isNavItemActive = (path) => {
    if (path === "/") return location.pathname === "/";
    const current = location.pathname.replace(/\/?$/, "/");
    if (path === ministryPath) {
      return current.startsWith(ministryPath) && !current.startsWith(newsPath);
    }
    return current.startsWith(path);
  };

  const mobileNavItems = navItems;

  // Footer (versión B, 2026-09-29): una sola fila, solo destinos que no están en navItems.
  const footerLinks = [
    { to: buildPath("/equipo/"), label: t('footer.team') },
    { to: "/fruto/", label: t('footer.fruto') },
    { to: buildPath("/tienda/"), label: t('navigation.store') },
    { to: "/apoya/", label: t('footer.give') },
    { to: "/newsletter/", label: t('navigation.newsletter') },
  ];

  const navHeight = 'pb-[61px] sm:pb-[62px]';
  const playerHeight = 'pb-[149px] sm:pb-[150px]';

  // Safe area: use CSS environment variables on native apps instead of fixed values
  // Tailwind bracket notation keeps these classes safe from purging
  const nativeSafeArea = isNativeApp ? {
    paddingTop: "pt-[env(safe-area-inset-top)]",
    paddingBottom: isIOS ? "pb-[env(safe-area-inset-bottom)]" : "",
    playerPadding: track
      ? (isIOS ? `pb-[177px] sm:pb-[185px] pb-[env(safe-area-inset-bottom)]` : `pb-[177px] sm:pb-[185px]`)
      : (isIOS ? `pb-[89px] sm:pb-[97px] pb-[env(safe-area-inset-bottom)]` : `pb-[89px] sm:pb-[97px]`)
  } : {
    paddingTop: '',
    paddingBottom: '',
    playerPadding: track ? playerHeight : navHeight
  };

  // Padding para nav sólo si es PWA (no nativo)
  const navPadding = isPWA && !isNativeApp ? 'pb-6' : '';

  return (
    <div className="bg-brand-parchment dark:bg-brand-dark min-h-screen">
      {/* Barra superior móvil — franja fija, no un ícono suelto flotando sobre la foto.
          Reemplaza la burbuja de cuenta sin etiqueta que existía antes (feedback de Víctor,
          2026-08-21: "queda la burbuja de cuenta flotando sin contacto, no se entiende"). Ver
          project-log/initiatives/barra-cuenta-descubrible.md. Con sesión, reabre a propósito lo
          que U1 (2026-08-05) había retirado (Ajustes+campana en móvil) — decisión explícita del
          usuario para cerrar el gap de notificaciones que quedaba pendiente para U3. El ítem 1 de
          navItems (Inicio/Hoy) no se toca, sigue siendo el acceso principal al panel.
          Posicionamiento espeja al header de escritorio: `absolute` por defecto (se va con el
          scroll, no permanece pegada en pantalla — feedback Víctor 2026-08-21, "el botón no debe
          permanecer"), `fixed` con auto-hide solo en /devocionales/ (mismo `navHidden` que ya usa
          el header de escritorio para ganar espacio de lectura).
          Sin sesión ya no se muestra (versión B, Víctor 2026-09-30: dos pastillas flotando se veían
          sueltas y la barra de abajo ya resuelve la navegación). Entrar e idioma quedan en una
          línea sutil bajo el botón del hero y en el footer. Con sesión se queda: ahí viven
          Ajustes, Mensajes, Admin y los avisos, que no tienen otro acceso en móvil. */}
      {user && (
      <div
        className={`md:hidden left-0 right-0 z-50 flex items-center justify-between px-3 transition-all duration-300 ${
          isDevotionalPage ? 'fixed' : 'absolute'
        } ${
          isDevotionalPage && navHidden ? '-translate-y-full' : 'translate-y-0'
        } top-0 ${
          isHome
            ? 'bg-gradient-to-b from-black/40 via-black/10 to-transparent'
            : 'bg-brand-parchment dark:bg-brand-dark border-b border-brand-gold/20 dark:border-brand-forest'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(3rem + env(safe-area-inset-top))' }}
      >
        {!isHome ? (
          <Link to="/" aria-label={t('app_name')} className="flex-shrink-0">
            <img
              src={language === 'en' ? "/logo/EN-circle.PNG" : "/logo/ES-circle.PNG"}
              alt={t('logo_alt')}
              className="w-8 h-8 rounded-full"
              loading="lazy"
            />
          </Link>
        ) : <span />}

        <div className="flex items-center gap-1.5">
          {/* Cambiador de idioma — mismo patrón que el globo del navbar de escritorio, faltaba
              en la barra móvil (feedback Víctor 2026-08-21). Visible con y sin sesión. */}
          <div className="relative" ref={mobileLangMenuRef}>
            <button
              type="button"
              onClick={() => setMobileLangMenuOpen((v) => !v)}
              aria-label={t('change_language')}
              title={t('change_language')}
              aria-expanded={mobileLangMenuOpen}
              className={`flex items-center justify-center p-2 rounded-full shadow-sm transition-colors text-brand-forest dark:text-brand-parchment ${
                isHome ? 'bg-white/90 dark:bg-brand-dark-card/90 backdrop-blur' : 'bg-white dark:bg-brand-dark-card'
              }`}
            >
              <GlobeIcon className="w-5 h-5" />
            </button>
            {mobileLangMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-36 bg-white dark:bg-brand-dark-card rounded-xl shadow-lg z-50 ring-1 ring-brand-gold/20 focus:outline-none overflow-hidden">
                <div className="py-1">
                  <button
                    type="button"
                    onClick={() => { handleLanguageSelect('es'); setMobileLangMenuOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-brand-verse dark:hover:bg-brand-forest transition ${!isEnglish ? 'font-semibold text-brand-forest dark:text-brand-gold' : 'text-brand-forest dark:text-brand-parchment'}`}
                  >
                    {t('language_spanish')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { handleLanguageSelect('en'); setMobileLangMenuOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-brand-verse dark:hover:bg-brand-forest transition ${isEnglish ? 'font-semibold text-brand-forest dark:text-brand-gold' : 'text-brand-forest dark:text-brand-parchment'}`}
                  >
                    {t('language_english')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {user ? (
            <>
            {isAdmin && (
              <Link
                to="/admin/"
                aria-label={t('admin.panelTitle')}
                className={`relative flex items-center justify-center w-9 h-9 rounded-full shadow-sm text-brand-forest dark:text-brand-parchment ${
                  isHome ? 'bg-white/90 dark:bg-brand-dark-card/90 backdrop-blur' : 'bg-white dark:bg-brand-dark-card'
                }`}
              >
                <ShieldCheckIcon className="w-5 h-5" aria-hidden="true" />
                {pendingGraduatesCount > 0 && (
                  <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-brand-gold ring-2 ring-white dark:ring-brand-dark" aria-hidden="true" />
                )}
              </Link>
            )}
            {/* Voces: un toque a Mis grabaciones (cuenta-simple-equipo.md, 2026-10-26). */}
            {isVoiceMember && (
              <Link
                to="/grabar/"
                aria-label={t('account.voiceTitle')}
                className={`flex items-center justify-center w-9 h-9 rounded-full shadow-sm text-brand-forest dark:text-brand-parchment ${
                  isHome ? 'bg-white/90 dark:bg-brand-dark-card/90 backdrop-blur' : 'bg-white dark:bg-brand-dark-card'
                }`}
              >
                <MicrophoneIcon className="w-5 h-5" aria-hidden="true" />
              </Link>
            )}
            {/* Cuenta como desplegable, claro desde el inicio (texto + flecha) — antes era un
                link directo sin ninguna señal de que se desplegaba nada (feedback Víctor
                2026-08-21). Admin vive adentro del menú, ya no como ícono suelto aparte:
                menos piezas, mismo patrón que el "Mi cuenta ▾" de escritorio. */}
            <div className="relative" ref={mobileAccountMenuRef}>
              <button
                type="button"
                onClick={() => setMobileAccountMenuOpen((v) => !v)}
                aria-label={t('auth.myAccount')}
                aria-expanded={mobileAccountMenuOpen}
                className={`flex items-center gap-1 pl-2 pr-2.5 py-1.5 rounded-full shadow-sm transition-colors text-brand-forest dark:text-brand-parchment ${
                  isHome ? 'bg-white/90 dark:bg-brand-dark-card/90 backdrop-blur' : 'bg-white dark:bg-brand-dark-card'
                }`}
              >
                <span className="text-sm font-semibold whitespace-nowrap">{t('auth.myAccount')}</span>
                <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${mobileAccountMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileAccountMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-brand-dark-card rounded-xl shadow-lg border border-brand-gold/10 dark:border-brand-forest py-1.5 z-50">
                  <Link
                    to="/mi-cuenta/"
                    className="block px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors"
                    onClick={() => setMobileAccountMenuOpen(false)}
                  >
                    {SHOW_HOY ? t('notifications.today') : t('account.menuLabel')}
                  </Link>
                  <Link
                    to="/ajustes/"
                    className="block px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors"
                    onClick={() => setMobileAccountMenuOpen(false)}
                  >
                    {t('navigation.settings')}
                  </Link>
                  {SHOW_HOY && (
                    <Link
                      to="/mi-cuenta/mensajes/"
                      className="block px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors"
                      onClick={() => setMobileAccountMenuOpen(false)}
                    >
                      {t('notifications.viewAllMessages')}
                    </Link>
                    )}
                  {SHOW_HOY && isAdmin && (
                    <Link
                      to="/admin/"
                      className="block px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors"
                      onClick={() => setMobileAccountMenuOpen(false)}
                    >
                      {t('admin.panelTitle')}
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => { setMobileAccountMenuOpen(false); logoutUser(); }}
                    className="block w-full text-left px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors border-t border-brand-gold/10 dark:border-brand-forest mt-1 pt-2.5"
                  >
                    {t('auth.logout')}
                  </button>
                </div>
              )}
            </div>
            <NotificationCapsule
              variant="mobile"
              floating={false}
              signalsOnly
              signals={notificationSignals}
            />
            </>
          ) : null}
        </div>
      </div>
      )}

      {/* Navbar superior — solo desktop. absolute por defecto (scrollea con la página, no se queda pegado arriba).
          En el home ("/") es transparente y se funde con la foto del hero, como referencia HeartCry;
          en el resto de páginas usa fondo sólido para mantener legibilidad sobre fondos claros.
          En /devocionales/ es fixed con auto-hide: se oculta al bajar, reaparece al subir (más espacio de lectura). */}
      <header
        className={`print:hidden hidden md:block left-0 right-0 z-50 transition-all duration-300 ${
          isDevotionalPage ? 'fixed' : 'absolute'
        } ${
          isDevotionalPage && navHidden ? '-translate-y-full' : 'translate-y-0'
        } ${
          isHome
            ? 'top-0 bg-transparent border-b border-transparent'
            : 'top-0 bg-brand-parchment dark:bg-brand-dark border-b border-brand-gold/30 dark:border-brand-forest'
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center gap-6">
          {/* Logo — vuelve a "/" desde cualquier página, sesión o no (navegacion-hoy-entrada-salida.md).
              Izquierda, no un link de texto "Inicio" adicional: con sesión, navItems[0] ya no apunta a
              "/" (es "Hoy"), y duplicar el link de home en dos lugares del mismo nav es ruido, no ayuda
              (NN/g). Se omite en el propio home para no repetir el logo gigante del hero, debajo. */}
          {!isHome && (
            <Link to="/" aria-label={t('app_name')} className="flex-shrink-0">
              <img
                src={language === 'en' ? "/logo/EN-circle.PNG" : "/logo/ES-circle.PNG"}
                alt={t('logo_alt')}
                className="w-10 h-10 rounded-full"
                loading="lazy"
              />
            </Link>
          )}

          {/* Links */}
          <nav className="flex items-center gap-6 flex-1">
            {navItems.map((item) => {
              const linkColorClass = isHome
                ? 'text-brand-parchment/90 hover:text-brand-gold'
                : 'text-brand-sage dark:text-brand-sage hover:text-brand-forest dark:hover:text-brand-gold';
              const activeColorClass = isHome ? '!text-brand-gold' : '!text-brand-forest dark:!text-brand-gold';

              // Ítem 1 con sesión ("Hoy") lleva tratamiento de botón, no de link de texto —
              // hoy-jerarquia-visual.md: sin esto, la puerta al panel se confunde con
              // cualquier otro ítem de contenido. Forest (no dorado) para no competir
              // visualmente con "Aporta", el único CTA dorado del navbar. Sobre la foto del
              // hero (navbar transparente en home) forest-sobre-forest se pierde, así que ahí
              // se invierte a parchment sólido con texto forest — mismo botón, contraste real.
              // En reposo es outline (mismo sistema de "página activa" que el resto del nav);
              // solo se rellena cuando de verdad estás en /mi-cuenta/ — antes el relleno era
              // fijo y "Hoy" se veía resaltado en cualquier página (feedback 2026-08-13).
              // activeClassName usa !important porque Tailwind no garantiza que las clases del
              // estado activo ganen en cascada sobre las del botón base (mismo patrón que
              // activeColorClass más abajo).
              if (item.path === homePath && showHoyNav) {
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`text-sm font-bold px-4 py-1.5 rounded-full border-2 transition-colors ${
                      isHome
                        ? 'border-brand-parchment/50 text-brand-parchment/90 hover:border-brand-parchment hover:text-brand-parchment'
                        : 'border-brand-sage/40 text-brand-sage hover:border-brand-forest hover:text-brand-forest dark:hover:border-brand-gold dark:hover:text-brand-gold'
                    }`}
                    activeClassName={`!border-brand-forest !bg-brand-forest !text-brand-parchment hover:!bg-brand-forest-light dark:!border-brand-gold dark:!bg-brand-gold dark:!text-brand-forest dark:hover:!bg-brand-gold/90`}
                    partiallyActive
                  >
                    {item.name}
                  </Link>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-sm font-semibold transition-colors ${linkColorClass} ${isNavItemActive(item.path) ? activeColorClass : ''}`}
                  aria-current={isNavItemActive(item.path) ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Tienda + Aporta: solo sin sesión — feedback de Víctor en /mi-cuenta/ (2026-08-12):
              logueado, ese espacio compite con los controles de cuenta y ninguno de los dos es
              lo que alguien logueado busca ahí. Con sesión, ese espacio queda libre para
              admin/ajustes/campana/logout. navegacion-hoy-entrada-salida.md */}
          <div className="flex items-center gap-4">
            {!user && (
              <Link
                to={buildPath("/tienda/")}
                className={`text-sm font-semibold transition-colors ${
                  isHome
                    ? 'text-brand-parchment/90 hover:text-brand-gold'
                    : 'text-brand-sage dark:text-brand-sage hover:text-brand-forest dark:hover:text-brand-gold'
                }`}
                activeClassName={isHome ? '!text-brand-gold' : '!text-brand-forest dark:!text-brand-gold'}
              >
                {t('navigation.store')}
              </Link>
            )}
            <div className="relative" ref={langMenuRef}>
              <button
                type="button"
                onClick={() => setLangMenuOpen((v) => !v)}
                aria-label={t('change_language')}
                title={t('change_language')}
                aria-expanded={langMenuOpen}
                className={`p-2 rounded-full transition-colors ${
                  isHome
                    ? 'text-brand-parchment/90 hover:text-brand-gold'
                    : 'text-brand-sage dark:text-brand-sage hover:text-brand-forest dark:hover:text-brand-gold'
                } ${langMenuOpen ? (isHome ? 'bg-white/15' : 'bg-brand-verse dark:bg-brand-forest') : ''}`}
              >
                <GlobeIcon className="w-5 h-5" />
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-36 bg-white dark:bg-brand-dark-card rounded-xl shadow-lg z-50 ring-1 ring-brand-gold/20 focus:outline-none overflow-hidden">
                  <div className="py-1">
                    <button
                      type="button"
                      onClick={() => { handleLanguageSelect('es'); setLangMenuOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-brand-verse dark:hover:bg-brand-forest transition ${!isEnglish ? 'font-semibold text-brand-forest dark:text-brand-gold' : 'text-brand-forest dark:text-brand-parchment'}`}
                    >
                      {t('language_spanish')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { handleLanguageSelect('en'); setLangMenuOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-brand-verse dark:hover:bg-brand-forest transition ${isEnglish ? 'font-semibold text-brand-forest dark:text-brand-gold' : 'text-brand-forest dark:text-brand-parchment'}`}
                    >
                      {t('language_english')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            {!user && (
              <Link
                to="/apoya/"
                className="bg-brand-gold hover:bg-brand-gold-dark text-brand-dark text-sm font-bold px-5 py-2 rounded-full shadow-[0_3px_0_0_rgba(184,131,26,1)] hover:shadow-[0_1px_0_0_rgba(184,131,26,1)] hover:translate-y-0.5 transition-all duration-150 whitespace-nowrap border border-brand-gold-dark/40"
              >
                {t('footer.give')}
              </Link>
            )}
            {/* Sin sesión el ícono no comunica que ahí se entra: se muestra la palabra.
                Con sesión: campana de notificaciones separada (es un concepto distinto, avisos,
                no gestión de cuenta) + un único menú "Mi cuenta" que agrupa Ajustes/Admin/Cerrar
                sesión — antes eran 3 piezas sueltas sin agrupación visual (escudo de admin, ícono
                de cuenta fusionado con la campana, botón de texto de logout), reportado como
                confuso por Víctor. project-log/initiatives/barra-cuenta-descubrible.md */}
            {user ? (
              <>
                {/* Acceso directo al panel para admins (pedido de Víctor 2026-09-23: que quien
                    administre no tenga que buscarlo dentro del menú). El ítem del menú "Cuenta"
                    se queda igual; esto es solo un atajo visible. Punto dorado = hay egresados
                    de FRUTO listos para promoción. */}
                {isAdmin && (
                  <Link
                    to="/admin/"
                    className={`relative inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                      isHome
                        ? 'bg-white/15 text-brand-parchment hover:bg-white/25'
                        : 'bg-brand-forest text-brand-parchment hover:bg-brand-forest-light dark:bg-brand-gold dark:text-brand-forest'
                    }`}
                  >
                    <ShieldCheckIcon className="w-4 h-4" aria-hidden="true" />
                    {t('admin.navButton')}
                    {pendingGraduatesCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-brand-gold ring-2 ring-white dark:ring-brand-dark" aria-hidden="true" />
                    )}
                  </Link>
                )}
                {isVoiceMember && (
                  <Link
                    to="/grabar/"
                    className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                      isHome
                        ? 'bg-white/15 text-brand-parchment hover:bg-white/25'
                        : 'bg-brand-forest text-brand-parchment hover:bg-brand-forest-light dark:bg-brand-gold dark:text-brand-forest'
                    }`}
                  >
                    <MicrophoneIcon className="w-4 h-4" aria-hidden="true" />
                    {t('account.recordButton')}
                  </Link>
                )}
                <NotificationCapsule
                  variant="desktop"
                  isHome={isHome}
                  signals={notificationSignals}
                  signalsOnly
                />
                <div className="relative" ref={accountMenuRef}>
                  <button
                    type="button"
                    onClick={() => setAccountMenuOpen((v) => !v)}
                    aria-label={t('auth.myAccount')}
                    aria-expanded={accountMenuOpen}
                    className={`flex items-center gap-1 pl-2 pr-2.5 py-1.5 rounded-full transition-colors whitespace-nowrap ${
                      isHome
                        ? 'text-brand-parchment/90 hover:text-brand-gold'
                        : 'text-brand-sage dark:text-brand-sage hover:text-brand-forest dark:hover:text-brand-gold'
                    } ${accountMenuOpen ? (isHome ? 'bg-white/15' : 'bg-brand-verse dark:bg-brand-forest') : ''}`}
                  >
                    <UserCircleIcon className="w-5 h-5" />
                    <span className="text-sm font-semibold">{t('auth.myAccount')}</span>
                    <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${accountMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {accountMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white dark:bg-brand-dark-card rounded-xl shadow-lg border border-brand-gold/10 dark:border-brand-forest py-1.5 z-50">
                      <Link
                        to="/mi-cuenta/"
                        className="block px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        {SHOW_HOY ? t('notifications.today') : t('account.menuLabel')}
                      </Link>
                      <Link
                        to="/ajustes/"
                        className="block px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        {t('navigation.settings')}
                      </Link>
                      {SHOW_HOY && (
                        <Link
                          to="/mi-cuenta/mensajes/"
                          className="block px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors"
                          onClick={() => setAccountMenuOpen(false)}
                        >
                          {t('notifications.viewAllMessages')}
                        </Link>
                        )}
                      {SHOW_HOY && isAdmin && (
                        <Link
                          to="/admin/"
                          className="block px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors"
                          onClick={() => setAccountMenuOpen(false)}
                        >
                          {t('admin.panelTitle')}
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => { setAccountMenuOpen(false); logoutUser(); }}
                        className="block w-full text-left px-4 py-2 text-sm font-medium text-brand-forest dark:text-brand-parchment hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors border-t border-brand-gold/10 dark:border-brand-forest mt-1 pt-2.5"
                      >
                        {t('auth.logout')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link
                to="/mi-cuenta/"
                className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full shadow-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                  isHome
                    ? 'bg-white/15 backdrop-blur text-brand-parchment hover:text-brand-gold'
                    : 'bg-white dark:bg-brand-dark-card text-brand-forest dark:text-brand-parchment hover:text-brand-gold-dark dark:hover:text-brand-gold'
                }`}
              >
                <UserCircleIcon className="w-5 h-5" />
                {t('auth.signIn')}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className={`transition-all duration-300 ${nativeSafeArea.playerPadding} ${track ? 'md:pb-[97px]' : 'md:pb-0'} ${isHome ? '' : `${user ? 'pt-[calc(3rem+env(safe-area-inset-top))]' : 'pt-[env(safe-area-inset-top)]'} md:pt-16`}`}>
        <div className={isHome || startsWithBand ? '' : 'pt-4 sm:pt-6 md:pt-6'}>
          {children}
        </div>

        {/* Footer — "Centrado mínimo" (versión B, Víctor 2026-09-29). Solo lo que no está en la
            navegación: Equipo, FRUTO, Tienda, Aporta y Boletín en una fila (etiquetas cortas para
            que quepa en una línea en móvil), los íconos y la línea legal, todo centrado. Salieron
            "Contenido" y "Tu cuenta" (repetían la navegación y el menú de cuenta), la descripción
            y el correo escrito. FRUTO se queda: desde que se ocultó "Camina con nosotros" del
            home, este es su camino principal. Visible también en móvil: el padding inferior de
            <main> deja libre la franja de la tab bar/reproductor. */}
        <footer className="border-t border-brand-gold/20 dark:border-brand-forest bg-brand-parchment dark:bg-brand-dark">
          <div className="max-w-6xl mx-auto px-6 pt-9 pb-20 md:pb-8 flex flex-col items-center gap-5 text-center">
            <Link to={buildPath("/")} className="flex flex-col items-center gap-2">
              <img
                src={language === 'en' ? "/logo/EN-circle.PNG" : "/logo/ES-circle.PNG"}
                alt={t('logo_alt')}
                className="w-11 h-11 rounded-full"
                loading="lazy"
              />
              <span className="font-bold text-xs tracking-wide text-brand-forest dark:text-brand-parchment uppercase">
                {t('app_name')}
              </span>
            </Link>

            <nav aria-label={t('footer.navAria')} className="flex flex-wrap justify-center gap-x-4 sm:gap-x-6 gap-y-2 text-sm text-brand-sage dark:text-brand-parchment/60">
              {footerLinks.map((link) => (
                <Link key={link.to} to={link.to} className="whitespace-nowrap hover:text-brand-forest dark:hover:text-brand-parchment transition-colors">
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              {[
                { href: YOUTUBE_URL, label: t('footer.youtube'), Icon: YouTubeIcon, size: "w-5 h-5", external: true },
                { href: WHATSAPP_URL, label: t('footer.whatsapp'), Icon: WhatsAppIcon, size: "w-[18px] h-[18px]", external: true },
                { href: `mailto:${CONTACT_EMAIL}`, label: t('footer.email'), Icon: EnvelopeIcon, size: "w-5 h-5", external: false },
              ].map(({ href, label, Icon, size, external }) => (
                <a
                  key={href}
                  href={href}
                  {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  aria-label={label}
                  title={external ? label : CONTACT_EMAIL}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-white dark:bg-brand-dark-card text-brand-sage dark:text-brand-parchment/70 hover:text-brand-forest dark:hover:text-brand-parchment transition-colors shadow-sm"
                >
                  <Icon className={size} />
                </a>
              ))}
            </div>

            <div className="w-full pt-5 mt-1 border-t border-brand-gold/10 dark:border-brand-forest/50 flex flex-col items-center gap-2 text-xs text-brand-sage dark:text-brand-parchment/50">
              <span>&copy; {new Date().getFullYear()} {t('app_name')}. {t('footer.rights')}</span>
              <div className="flex flex-wrap justify-center items-center gap-x-5 gap-y-2">
                {/* Sin sesión, entrar vive aquí (y bajo el botón del hero) desde que la barra
                    superior móvil se muestra solo con sesión. */}
                {!user && (
                  <Link to="/mi-cuenta/" className="hover:text-brand-forest dark:hover:text-brand-parchment transition-colors">
                    {t('auth.signIn')}
                  </Link>
                )}
                <Link to="/politica-de-privacidad/" className="hover:text-brand-forest dark:hover:text-brand-parchment transition-colors">
                  {t('footer.privacy')}
                </Link>
                <Link to="/como-protegemos-tu-privacidad/" className="hover:text-brand-forest dark:hover:text-brand-parchment transition-colors">
                  {t('footer.privacyModel')}
                </Link>
                {/* El sitio hermano se oculta en la app nativa: ahí abriría otro sitio dentro del
                    propio webview y el usuario quedaría fuera de la app. */}
                {!isNativeApp && (
                  <a
                    href={getSiblingUrl(location.pathname, getBuildLanguage())}
                    onClick={(e) => {
                      // Con sesión, mismo aviso que el globo: el otro dominio no conserva la sesión.
                      if (!user) return;
                      e.preventDefault();
                      setPendingLangSwitch(getBuildLanguage() === 'en' ? 'es' : 'en');
                    }}
                    aria-label={t('footer.siblingSiteAria')}
                    className="hover:text-brand-forest dark:hover:text-brand-parchment transition-colors"
                  >
                    {t('footer.siblingSite')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </footer>
      </main>

      <footer className="print:hidden fixed bottom-0 left-0 right-0 z-50">
        <PlayerUI />
        <nav
          className={`md:hidden w-full bg-brand-parchment dark:bg-brand-dark border-t border-brand-gold/30 dark:border-brand-forest shadow-top-md ${navPadding}`}
          style={isIOS ? { paddingBottom: 'env(safe-area-inset-bottom)' } : {}}
        >
          <div className="flex justify-around max-w-lg mx-auto pt-1 pb-2 items-start">
            {mobileNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex flex-col items-center flex-1 px-1 text-center focus:outline-none transition-all duration-200 ease-in-out group text-brand-sage dark:text-brand-sage hover:text-brand-forest dark:hover:text-brand-gold pt-2 ${
                    isNavItemActive(item.path) ? '!text-brand-forest dark:!text-brand-gold scale-105' : ''
                  }`}
                  aria-current={isNavItemActive(item.path) ? 'page' : undefined}
                >
                  <Icon className="w-6 h-6 mb-0.5 transition-transform duration-200 ease-in-out group-hover:scale-110" />
                  <span className="text-[11px] sm:text-xs font-medium leading-tight text-center transition-opacity duration-200 ease-in-out opacity-90 group-hover:opacity-100">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </footer>

      {/* Widget global de feedback (S18/feedback-nombre-newsletter-qr.md): oculto desde el
          2026-10-14 porque no se usa (decisión de Víctor). El componente, /reportar/ y
          /admin/desarrollo/ siguen vivos; para volver a mostrarlo, SHOW_FEEDBACK_WIDGET = true. */}
      {SHOW_FEEDBACK_WIDGET && user && !location.pathname.startsWith('/admin') && <FeedbackWidget />}

      {/* Tarjeta de un solo aviso del devocional por correo: solo visitantes sin sesión, en
          rutas públicas y fuera de la app nativa (ahí el devocional ya llega por push). */}
      {!user && !isNativeApp && showSubscribeCard && (
        <SubscribeCard pathname={location.pathname} playerActive={!!track} />
      )}

      {/* Aviso de cambio de idioma con sesión abierta — ver handleLanguageSelect. Clic en el
          fondo o Escape = "Quedarme aquí". */}
      {pendingLangSwitch && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-brand-dark/50"
          onClick={() => setPendingLangSwitch(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="lang-switch-title"
            className="w-full max-w-md bg-white dark:bg-brand-dark-card rounded-2xl shadow-xl p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-11 h-11 rounded-full bg-brand-verse text-brand-gold-dark flex items-center justify-center mb-4">
              <GlobeIcon className="w-6 h-6" />
            </div>
            <h2 id="lang-switch-title" className="font-black text-xl leading-tight text-brand-forest dark:text-brand-parchment mb-3">
              {t(pendingLangSwitch === 'en' ? 'languageSwitch.toEn.title' : 'languageSwitch.toEs.title')}
            </h2>
            <p className="text-sm leading-relaxed text-brand-sage dark:text-brand-parchment/75 mb-6">
              {t(pendingLangSwitch === 'en' ? 'languageSwitch.toEn.body' : 'languageSwitch.toEs.body')}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={confirmLangSwitch}
                className="bg-brand-forest hover:bg-brand-forest-light text-brand-parchment text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
              >
                {t(pendingLangSwitch === 'en' ? 'languageSwitch.toEn.go' : 'languageSwitch.toEs.go')}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => setPendingLangSwitch(null)}
                className="border-2 border-brand-forest/20 dark:border-brand-parchment/30 text-brand-forest dark:text-brand-parchment hover:border-brand-forest dark:hover:border-brand-parchment text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
              >
                {t('languageSwitch.stay')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}